import * as React from 'react';
import { ReorderContext } from './ReorderContext';
import { 
  Droppable, 
  Draggable, 
  DraggablePressEvent, 
  DraggableDragStartEvent, 
  DraggableDragEvent, 
  DroppableDragOverEvent 
} from '@progress/kendo-react-common';
import { DRAGGABLE_ROW_PROPS, DRAGGABLE_ROW_STATE } from './Types';

export default class DraggableRow extends React.Component<DRAGGABLE_ROW_PROPS, DRAGGABLE_ROW_STATE> {
  private element = React.createRef<HTMLTableRowElement>();

  constructor(props) {
    super(props);

    this.state = {
      dragged: false,
      dropped: false,
      direction: null,
      initial: { x: 0, y: 0 }
    };
  }

  handlePress = ({ event }: DraggablePressEvent) => {
    this.setState({ 
      initial: { 
        x: event.clientX - event.offsetX, 
        y: event.clientY - event.offsetY 
      }
    });
  };

  handleDragStart = ({ event }: DraggableDragStartEvent) => {
    if (!event.originalEvent.target || !(event.originalEvent.target as HTMLElement).dataset.dragHandle) { 
      return; 
    }
    this.setState({ dragged: true });
  };

  handleDrag = ({ event }: DraggableDragEvent) => {
    if (!this.element.current || !this.state.dragged) { 
      return; 
    }
    this.element.current.style.transform = `translateY(${event.clientY - this.state.initial.y + event.scrollY}px)`;
  };

  handleDragEnd = () => {
    this.setState({
      dragged: false,
      dropped: false,
      initial: { x: 0, y: 0 }
    });
  };

  handleRelease = () => {
    if (!this.element.current) { 
      return; 
    }
    this.element.current.style.transform = null;
  };

  handleDragEnter = () => {
    this.setState({
      dropped: true,
      direction: null
    });
  };

  handleDragOver = ({ event }: DroppableDragOverEvent) => {
    if (!this.element.current) { 
      return; 
    }
    const rect = this.element.current.getBoundingClientRect();
    this.setState({ 
      direction: (rect.top + (rect.height / 2)) <= event.pageY ? 'after' : 'before' 
    });
  };

  handleDragLeave = () => {
    this.handleDrop();
  };

  handleDrop = () => {
    this.setState({
      dropped: false,
      direction: null
    });
  }

  render() {
    return (
      <ReorderContext.Consumer>
        {({ reorder, dragStart }) => (
          <React.Fragment>
            {(this.state.dropped && this.state.direction === 'before') && <tr 
              style={{ 
                outlineStyle: 'solid', 
                outlineWidth: 1, 
                outlineColor: 'red' 
              }}
            />}
            <Droppable
              onDragEnter={this.handleDragEnter}
              onDragOver={this.handleDragOver}
              onDragLeave={this.handleDragLeave}
              onDrop={() => {
                reorder(this.props.dataItem, this.state.direction);
                this.handleDrop();
              }}
            >
              <Draggable
                childRef={this.element}
                autoScroll={this.state.dragged}
                onPress={this.handlePress}
                onDragStart={(event: DraggableDragStartEvent) => {
                  this.handleDragStart(event);
                  dragStart(this.props.dataItem);
                }}
                onDrag={this.handleDrag}
                onDragEnd={this.handleDragEnd}
                onRelease={this.handleRelease}
              >
                <tr
                  {...this.props.elementProps}
                  style={{
                    backgroundColor: '#fff',
                    userSelect: 'none',
                    pointerEvents: this.state.dragged ? 'none' : undefined,
                    opacity: this.state.dragged ? '0.8' : undefined
                  }}
                >
                  {this.props.children}
                </tr>
              </Draggable>
            </Droppable>
            {(this.state.dropped && this.state.direction === 'after') && <tr 
              style={{ 
                outlineStyle: 'solid', 
                outlineWidth: 1, 
                outlineColor: 'red' 
              }}
            />}
          </React.Fragment>
        )}
      </ReorderContext.Consumer>
    );
  }
}
